/**
 * File: storagePaths.ts
 * Path: packages/config/src/storage/storagePaths.ts
 * Description: Where files live in Cloud Storage, in one place. Every path to
 * a contract file, a lead/client/job document or a books receipt is made by a
 * function below; no other file builds one (storagePaths.test.ts fails if
 * one does). Moving files to a new layout is then a change to this file and
 * the migration, not a hunt through the app.
 *
 * The layout today:
 *   contracts/{contractId}/{file}
 *   documents/{leads|clients|jobs}/{ownerId}/{timestamp}-{file}
 *   accounting/{profileId}/receipts/{timestamp}-{file}
 */

import { CONTRACT_STORAGE_PREFIX } from "../contracts/contractConstants";
import { receiptFolder } from "../accounting/accountingRecord";

export type DocumentOwnerKind = "leads" | "clients" | "jobs";

/** A storage-safe file name: no slashes or odd characters, keeps the extension. */
export const safeFileName = (name: string): string => {
  const cleaned = name
    .normalize("NFKD")
    .replace(/[^\w.\- ]+/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/^\.+/, "")
    .slice(-120);
  return cleaned || "document";
};

/** Everything stored for one contract. */
export const contractFolder = (contractId: string): string => `${CONTRACT_STORAGE_PREFIX}/${contractId}`;

/** One stored file of a contract (a signature, a PDF). */
export const contractFilePath = (contractId: string, name: string): string => `${contractFolder(contractId)}/${name}`;

/** Every document attached to one lead, client or job. */
export const documentFolder = (kind: DocumentOwnerKind, ownerId: string): string => `documents/${kind}/${ownerId}`;

/** Where a new upload goes; the timestamp keeps two files with one name apart. */
export const documentPath = (kind: DocumentOwnerKind, ownerId: string, name: string, now: number): string =>
  `${documentFolder(kind, ownerId)}/${now}-${safeFileName(name)}`;

/** Where a receipt or bank statement for one set of books is stored. */
export const booksFilePath = (profileId: string, name: string, now: number): string =>
  `${receiptFolder(profileId)}/${now}-${name}`;

// ─── By business (data tidy: one folder per business, Liam 3 Nov 2026) ─────

/** Everything stored for one business; `folder` is businessFolderName(). */
export const businessStorageFolder = (folder: string): string => `businesses/${folder}`;

/** One stored file of a business's contract, e.g. signed.pdf, in the contract's own folder. */
export const businessContractFilePath = (folder: string, contractRef: string, name: string): string =>
  `${businessStorageFolder(folder)}/contracts/${contractRef}/${name}`;

/** A document attached to a business; the Firestore index sits at businesses/{id}/documents. */
export const businessDocumentPath = (folder: string, name: string, now: number): string =>
  `${businessStorageFolder(folder)}/documents/${now}-${safeFileName(name)}`;

/** A sent invoice PDF. */
export const businessInvoicePath = (folder: string, invoiceNumber: string, sentOn: string): string =>
  `${businessStorageFolder(folder)}/invoices/${invoiceNumber}/sent-${sentOn}.pdf`;

/** One screenshot of a website audit. */
export const businessAuditImagePath = (folder: string, auditId: string, n: number): string =>
  `${businessStorageFolder(folder)}/audits/${auditId}/${n}.webp`;
